import React, { useState, useEffect } from 'react';
import { FileDown } from 'lucide-react';

// Script d'appel CEE (Fiche CEE IND-BA-110 — Qualification industrie de production)
const CEE_SECTIONS = [
  {
    section_key: 'barrage',
    section_title: '0. Passer le barrage secrétaire (~20 sec)',
    ordre: 1,
    content: `ÉTAPE 0 — PASSAGE DU BARRAGE SECRÉTAIRE (~20 sec)

OPTION A — Dossier en cours (plus autoritaire) :
"Bonjour, j'appelle de la part du pôle technique CEE — j'ai un dossier en cours de traitement sur votre établissement, j'ai besoin de joindre votre responsable technique ou maintenance pour une vérification de référence."
→ À utiliser quand la secrétaire est ferme et filtre tous les appels commerciaux. Les expressions "dossier en cours" et "vérification de référence" donnent un caractère administratif à l'appel — elle ne prend pas le risque de bloquer quelque chose d'important. Ne laissez pas de message si absence — rappelez.

OPTION C — Question technique (plus neutre, passe facilement) :
"Bonjour, c'est [Prénom] — j'ai une question technique sur la configuration du chauffage industriel de votre site, j'aurais besoin de votre responsable maintenance ou production, c'est pour une étude sur votre établissement."
→ À utiliser en premier passage ou quand la secrétaire semble accessible. Formulation volontairement vague et non-commerciale — "étude sur votre site" suffit à décrocher le transfert sans éveiller de méfiance. Si elle demande "c'est à quel sujet exactement ?" : "C'est une étude énergétique réglementaire sur votre système de chauffage, votre responsable technique saura de quoi il s'agit."`
  },
  {
    section_key: 'accroche',
    section_title: '1. Accroche — présentation (~30 sec)',
    ordre: 2,
    content: `SCRIPT PRINCIPAL — ACCROCHE / PRÉSENTATION (~30 sec)

OPTION A — L'argent qui part au plafond :
"Bonjour, [Prénom] de [Société]. Je vous appelle car on intervient sur des sites de production comme le vôtre où on constate qu'entre 20 et 30 % du budget chauffage part littéralement au plafond - et on a un programme qui permet de récupérer ça sans que ça vous coûte un centime. Je voulais justement vérifier si votre site est concerné - vous chauffez avec une chaudière ou des aérothermes ?"

OPTION B — Le terrain direct :
"Bonjour, [Prénom] de [Société]. On travaille en ce moment avec plusieurs industriels de votre secteur sur la réduction de leurs charges énergétiques - certains ont divisé leur facture chauffage par 1,3 sans investir un euro. Je voulais voir si votre site est dans le même cas. Vous chauffez comment votre hall de production ?"

OPTION C — Le constat chiffré :
"Bonjour, [Prénom] de [Société]. Dans un hall de production chauffé en hauteur, on perd en moyenne 15 degrés entre le sol et la toiture - c'est l'argent qui chauffe le vide. On a un programme qui corrige ça gratuitement. Votre bâtiment fait quelle hauteur sous plafond ?"

→ Principe commun aux 3 options : on enchaîne directement sur la première question de qualification sans laisser le temps de dire non. Le prospect répond au lieu de raccrocher. En cas de résistance : "Je comprends - votre réponse me dira tout de suite si ça vous concerne ou pas, ça prend 30 secondes."`
  },
  {
    section_key: 'qualification',
    section_title: '2. Qualification — 3 critères production (~1 min)',
    ordre: 3,
    content: `QUALIFICATION — 3 CRITÈRES PRODUCTION (~1 min)

1) TYPE DE SITE ?
"C'est bien un site de production industrielle — atelier, hall de fabrication ?"
✅ OK : Site de production
⛔ STOP : Entrepôt → stop

2) CHAUFFAGE ?
"Vous chauffez avec une chaudière collective et/ou des aérothermes ?"
✅ OK : Chaudière / aérothermes
⛔ STOP : Poste / zone → stop

3) HAUTEUR DU LOCAL ?
"Votre bâtiment fait au moins 5 mètres sous plafond ou sous faîtage ?"
✅ OK : 5 m et + → éligible
⛔ STOP : Moins de 5 m → stop

→ Si les 3 réponses sont positives : le prospect est éligible. Ne perdez pas de temps sur un entrepôt logistique — ce n'est pas la cible. Concentrez-vous sur les ateliers, usines, halls de fabrication avec chauffage collectif.`
  },
  {
    section_key: 'accroche_valeur',
    section_title: '3. Accroche valeur — le problème concret (~40 sec)',
    ordre: 4,
    content: `ACCROCHE VALEUR — LE PROBLÈME CONCRET (~40 sec)

"Dans votre hall de production, l'air chaud de votre chaudière ou de vos aérothermes monte directement au plafond. En bas, au niveau des postes de travail, il fait froid et vos équipements chauffent à pleine puissance pour compenser. On parle d'un écart qui peut atteindre 15 degrés entre le sol et la toiture. Le déstratificateur redistribue cette chaleur perdue au niveau du sol — là où vous en avez besoin. Résultat : jusqu'à 30 % d'économies sur votre facture de chauffage. Et ça, on vous l'installe à zéro euro, sans avance de frais."`
  },
  {
    section_key: 'objections',
    section_title: '4. Gestion des objections',
    ordre: 5,
    content: `GESTION DES OBJECTIONS (si nécessaire)

— "On a déjà ça."
Très bien ! Ça date de quand ? Si l'installation a plus de 5 ans, les équipements ont évolué et vous avez peut-être droit à un remplacement ou un complément — toujours en CEE.

— "On n'a pas le temps."
Je comprends. Justement, c'est nous qui gérons tout : étude, dossier, installation. Votre seule action : signer le devis. 15 minutes pour un point technique, c'est tout.

— "On n'a pas de budget."
Aucun budget nécessaire. Zéro avance de frais, zéro remboursement. Le financement vient des Certificats d'Économies d'Énergie, pas de votre trésorerie.

— "C'est quoi les CEE ?"
C'est un dispositif légal encadré par le Ministère de la Transition Énergétique. Les fournisseurs d'énergie financent vos travaux d'économies d'énergie — c'est leur obligation légale.

— "Envoyez-moi une doc."
Je préfère vous faire un point rapide en visio ou sur site — 15 minutes — pour confirmer l'éligibilité de votre bâtiment. Vous êtes dispo mardi ou jeudi ?`
  },
  {
    section_key: 'closing',
    section_title: '5. Closing — prise de rendez-vous (~20 sec)',
    ordre: 6,
    content: `CLOSING — PRISE DE RENDEZ-VOUS (~20 sec)

Formulation de closing :
"Votre configuration est parfaite pour ce dispositif. Je vous propose un point de 15 minutes cette semaine — en visio ou sur site — pour qu'on regarde ensemble la configuration de votre hall et je vous confirme l'éligibilité. Vous avez un créneau mardi matin ou jeudi après-midi ?"

→ Toujours proposer deux créneaux précis. Ne demandez jamais "quand voulez-vous ?" — ça repousse la décision. Si refus : "Et la semaine prochaine ?" Pas plus de deux relances sur le créneau.`
  },
  {
    section_key: 'qualification_rapide',
    section_title: '6. Tableau de qualification rapide (GO / STOP)',
    ordre: 7,
    content: `TABLEAU DE QUALIFICATION RAPIDE — GO / NO-GO

TYPE DE SITE
✅ GO : Site de production industrielle
⛔ STOP : Entrepôt, logistique, tertiaire

CHAUFFAGE
✅ GO : Chaudière collective et/ou aérothermes
⛔ STOP : Chauffage de poste ou de zone uniquement

HAUTEUR
✅ GO : 5 m et plus sous plafond / faîtage
⛔ STOP : Moins de 5 m

LOCAL
✅ GO : Industriel existant chauffé
⛔ STOP : Construction neuve non chauffée`
  }
];

// Script d'appel STANDARD (E SHOPS PRO) — pour les agents classiques
const STANDARD_SECTIONS = [
  {
    section_key: 'introduction',
    section_title: '1. Introduction',
    ordre: 1,
    content: `"Bonjour, je suis [Prénom] de E SHOPS PRO, spécialiste en solutions digitales pour les entreprises. Suis-je bien en ligne avec M./Mme [Nom du gérant] ?"

SI OUI : Poursuivez avec l'accroche
SI NON (barrage secrétaire/standard) : "Je comprends. Pourriez-vous me transférer vers M./Mme [Nom] ? C'est au sujet d'une opportunité digitale pour votre entreprise."

Note : Si le gérant n'est pas disponible, proposez de rappeler : "Quand serait le meilleur moment pour le/la joindre ?"`
  },
  {
    section_key: 'accroche',
    section_title: '2. Accroche',
    ordre: 2,
    content: `"M./Mme [Nom], votre entreprise nous a été recommandée / nous avons identifié votre établissement comme ayant un fort potentiel de développement digital."

QUESTION CLE : "Avez-vous actuellement un site web ou une application mobile pour votre entreprise ?"

SCENARIOS :
- SI NON → "C'est justement pour ça que je vous appelle. E SHOPS PRO accompagne les entreprises comme la vôtre à développer leur présence digitale."
- SI OUI MAIS BASIQUE → "Excellent ! Et êtes-vous satisfait des résultats ? Génère-t-il suffisamment de contacts et de ventes ?"
- SI OUI ET SATISFAIT → "Parfait ! Savez-vous que nos solutions permettent d'aller encore plus loin avec des fonctionnalités exclusives ?"`
  },
  {
    section_key: 'presentation',
    section_title: '3. Présentation E SHOPS PRO',
    ordre: 3,
    content: `"E SHOPS PRO est une agence digitale spécialisée dans la création de solutions complètes pour les professionnels :

✅ Sites web vitrines et e-commerce professionnels
✅ Applications mobiles personnalisées (iOS & Android)
✅ Référencement local optimisé (Google My Business, SEO)
✅ Outils de gestion intégrés (CRM, facturation, planning)
✅ Système de notifications push pour fidéliser vos clients

Ce qui nous différencie : nous ne faisons pas que créer un site, nous créons un véritable outil de développement commercial adapté à votre activité."`
  },
  {
    section_key: 'application',
    section_title: "4. L'Application Mobile",
    ordre: 4,
    content: `"L'un de nos atouts majeurs est notre application mobile E SHOPS PRO Connect, téléchargeable sur tous les stores.

Pour vos clients :
📱 Accès direct à votre catalogue produits/services
📍 Géolocalisation et itinéraire vers votre établissement
📞 Contact en un clic (appel, email, WhatsApp)
⭐ Système d'avis et de fidélité intégré

Pour vous :
📊 Tableau de bord complet de votre activité
📨 Envoi de notifications push à vos clients
📅 Gestion des rendez-vous et réservations
💰 Suivi des commandes et paiements

Tout est centralisé dans une seule application !"`
  },
  {
    section_key: 'notifications',
    section_title: '5. Les Notifications Push',
    ordre: 5,
    content: `"Je voudrais vous parler d'une fonctionnalité exclusive : les notifications push.

Imaginez : vous avez une promotion, un événement, ou simplement une actualité à partager. En un clic depuis votre espace, vous envoyez une notification qui s'affiche directement sur le téléphone de TOUS vos clients ayant l'application.

C'est :
🎯 100% de taux de délivrabilité (contrairement aux emails)
⚡ Instantané (pas de délai comme les SMS)
💸 Illimité et inclus dans votre forfait
📈 Mesurable (vous voyez qui a ouvert)

Nos clients constatent en moyenne 40% d'augmentation de leur engagement client grâce à cette fonctionnalité."`
  },
  {
    section_key: 'objections',
    section_title: '6. Gestion des Objections',
    ordre: 6,
    content: `💰 "C'EST TROP CHER"
→ "Je comprends votre préoccupation. Justement, c'est pour ça que je vous propose un rendez-vous gratuit et sans engagement. Notre expert pourra vous présenter les différentes formules et vous montrer concrètement le retour sur investissement. Beaucoup de nos clients rentabilisent leur solution dès le premier mois."

⏰ "JE N'AI PAS LE TEMPS"
→ "Je comprends parfaitement, vous êtes très sollicité. C'est justement pour ça que le rendez-vous est court (20-30 min) et peut se faire par visioconférence si vous préférez. Quand auriez-vous un créneau cette semaine ou la semaine prochaine ?"

🤔 "J'AI DÉJÀ UN SITE / JE NE SUIS PAS INTÉRESSÉ"
→ "Je comprends. Puis-je simplement vous demander : êtes-vous satisfait du nombre de contacts/ventes que génère votre site actuel ? Nos solutions vont au-delà d'un simple site, c'est un véritable outil commercial. Le rendez-vous vous permettrait de découvrir des fonctionnalités dont vous n'avez peut-être pas connaissance."

📞 "ENVOYEZ-MOI UNE DOCUMENTATION"
→ "Bien sûr, je peux vous envoyer une brochure. Cependant, nos solutions sont personnalisées selon votre activité. Le rendez-vous de 20 minutes avec notre expert permettrait de vous présenter exactement ce qui correspond à vos besoins. Préférez-vous en début ou fin de semaine ?"

💭 "JE DOIS RÉFLÉCHIR"
→ "C'est tout à fait normal de prendre le temps de la réflexion. Le rendez-vous que je vous propose est justement là pour vous donner toutes les informations nécessaires, sans aucun engagement. Ainsi vous pourrez réfléchir avec tous les éléments en main."`
  },
  {
    section_key: 'rdv',
    section_title: '7. Prise de Rendez-vous',
    ordre: 7,
    content: `"Pour vous présenter tout cela en détail et surtout adapter notre solution à votre activité, je vous propose un rendez-vous avec l'un de nos experts.

Ce rendez-vous est :
✅ 100% GRATUIT
✅ Sans aucun engagement
✅ Court (20-30 minutes)
✅ Personnalisé à votre secteur d'activité

Il peut se faire :
📍 Directement à votre établissement
💻 En visioconférence (Zoom, Teams...)
☎️ Par téléphone si vous préférez

TRANSITION VERS LE RDV :
"Quel jour vous arrangerait le mieux cette semaine ou la semaine prochaine ?"
"Préférez-vous plutôt le matin ou l'après-midi ?"
"Êtes-vous disponible [proposer 2 créneaux] ?"`
  },
  {
    section_key: 'conclusion',
    section_title: '8. Conclusion & Validation',
    ordre: 8,
    content: `CONFIRMATION DU RDV :
"Parfait M./Mme [Nom], je récapitule :
📅 Rendez-vous le [DATE] à [HEURE]
📍 À [LIEU / Visioconférence]
👤 Avec notre expert [Nom de l'expert si connu]

Vous recevrez une confirmation par [email/SMS].
Avez-vous des questions avant notre rendez-vous ?"

SI REFUS DÉFINITIF :
"Je comprends tout à fait. Puis-je simplement vous laisser nos coordonnées au cas où vous changeriez d'avis ? Vous pouvez également consulter notre site e-application.fr pour en savoir plus. Je vous souhaite une excellente journée M./Mme [Nom]."

FIN D'APPEL :
"Merci pour votre temps M./Mme [Nom], et à très bientôt !"`
  }
];

function ScriptAppel({ scriptType = 'standard' }) {
  const SECTIONS = scriptType === 'cee' ? CEE_SECTIONS : STANDARD_SECTIONS;
  const [activeSection, setActiveSection] = useState(SECTIONS[0].section_key);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sections par défaut en cas d'erreur de chargement
  const defaultSections = SECTIONS.map(({ section_key, section_title, ordre }) => ({ section_key, section_title, ordre }));

  useEffect(() => {
    loadScript();
  }, [scriptType]);

  const loadScript = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/script-appel?type=${scriptType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setSections(data);
          setActiveSection(data[0].section_key);
        } else {
          // Utiliser les sections par défaut si la BDD est vide
          setSections(defaultSections.map(s => ({
            ...s,
            content: getDefaultContent(s.section_key)
          })));
        }
      } else {
        // En cas d'erreur, utiliser les sections par défaut
        setSections(defaultSections.map(s => ({
          ...s,
          content: getDefaultContent(s.section_key)
        })));
      }
    } catch (error) {
      console.error('Erreur chargement script:', error);
      // En cas d'erreur, utiliser les sections par défaut
      setSections(defaultSections.map(s => ({
        ...s,
        content: getDefaultContent(s.section_key)
      })));
    } finally {
      setLoading(false);
    }
  };

  const getDefaultContent = (key) => {
    const s = SECTIONS.find(x => x.section_key === key);
    return s ? s.content : '';
  };

  const getCurrentSection = () => {
    return sections.find(s => s.section_key === activeSection);
  };

  const formatContent = (content) => {
    if (!content) return null;

    // Convertir le contenu en éléments formatés
    return content.split('\n').map((line, index) => {
      // Lignes vides
      if (line.trim() === '') {
        return <br key={index} />;
      }

      // Lignes commençant par des emojis de check ou tirets
      if (line.match(/^[✅📱📍📞⭐📊📨📅💰🎯⚡💸📈💻☎️👤]/)) {
        return (
          <p key={index} style={{ margin: '8px 0', paddingLeft: '10px' }}>
            {line}
          </p>
        );
      }

      // Lignes commençant par - ou •
      if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
        return (
          <p key={index} style={{ margin: '8px 0', paddingLeft: '20px', color: '#555' }}>
            {line}
          </p>
        );
      }

      // Lignes commençant par → (réponses)
      if (line.trim().startsWith('→')) {
        return (
          <p key={index} style={{
            margin: '8px 0',
            paddingLeft: '20px',
            fontStyle: 'italic',
            color: '#2e7d32',
            background: '#e8f5e9',
            padding: '10px 15px',
            borderRadius: '8px',
            borderLeft: '3px solid #4caf50'
          }}>
            {line}
          </p>
        );
      }

      // Lignes en majuscules (titres de sous-section)
      if (line.match(/^[A-Z\s💰⏰🤔📞💭]{3,}:/) || line.match(/^SI\s/) || line.match(/^QUESTION\s/) || line.match(/^SCENARIOS\s/) || line.match(/^CONFIRMATION\s/) || line.match(/^TRANSITION\s/) || line.match(/^FIN\s/)) {
        return (
          <h4 key={index} style={{
            margin: '20px 0 10px',
            color: '#667eea',
            fontWeight: '600',
            borderBottom: '1px solid #e0e0e0',
            paddingBottom: '8px'
          }}>
            {line}
          </h4>
        );
      }

      // Texte entre guillemets (ce qu'on doit dire)
      if (line.trim().startsWith('"') && line.trim().endsWith('"')) {
        return (
          <div key={index} style={{
            background: '#e3f2fd',
            padding: '15px 20px',
            borderRadius: '10px',
            margin: '15px 0',
            borderLeft: '4px solid #1976d2',
            fontStyle: 'italic',
            fontSize: '1.05rem',
            color: '#1565c0'
          }}>
            {line}
          </div>
        );
      }

      // Note ou instruction
      if (line.toLowerCase().startsWith('note')) {
        return (
          <p key={index} style={{
            margin: '15px 0',
            padding: '12px 15px',
            background: '#fff3e0',
            borderRadius: '8px',
            borderLeft: '3px solid #ff9800',
            color: '#e65100'
          }}>
            {line}
          </p>
        );
      }

      // Lignes normales
      return (
        <p key={index} style={{ margin: '10px 0', lineHeight: '1.7', color: '#444' }}>
          {line}
        </p>
      );
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'white' }}>
        <p>Chargement du script...</p>
      </div>
    );
  }

  const currentSection = getCurrentSection();
  const currentIndex = sections.findIndex(s => s.section_key === activeSection);

  const goToPrev = () => {
    if (currentIndex > 0) {
      setActiveSection(sections[currentIndex - 1].section_key);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNext = () => {
    if (currentIndex < sections.length - 1) {
      setActiveSection(sections[currentIndex + 1].section_key);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const downloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/script-appel/pdf?type=${scriptType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `script-appel-${scriptType}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Erreur téléchargement PDF:', error);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '30px',
        borderRadius: '15px',
        marginBottom: '30px',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700' }}>{scriptType === 'cee' ? "Script d'Appel - CEE" : "Script d'Appel - E SHOPS PRO"}</h1>
          <p style={{ margin: '10px 0 0', opacity: 0.9 }}>{scriptType === 'cee' ? 'Qualification industrie de production - prospection telephonique CEE' : 'Guide complet pour la prospection telephonique'}</p>
        </div>
        <button
          onClick={downloadPDF}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: '2px solid white',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
          onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
        >
          <FileDown size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Télécharger PDF
        </button>
      </div>

      {/* Navigation des sections */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        marginBottom: '25px',
        background: 'white',
        padding: '15px',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
      }}>
        {sections.map((section, index) => (
          <button
            key={section.section_key}
            onClick={() => setActiveSection(section.section_key)}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              background: activeSection === section.section_key
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : '#f5f5f5',
              color: activeSection === section.section_key ? 'white' : '#333',
              transition: 'all 0.2s ease'
            }}
          >
            <span style={{ marginRight: '8px' }}>{index + 1}</span>
            {section.section_title.replace(/^\d+\.\s*/, '')}
          </button>
        ))}
      </div>

      {/* Contenu de la section active */}
      {currentSection && (
        <div style={{
          background: 'white',
          padding: '30px',
          borderRadius: '15px',
          boxShadow: '0 2px 15px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{
            color: '#667eea',
            borderBottom: '3px solid #667eea',
            paddingBottom: '10px',
            marginBottom: '25px'
          }}>
            {currentSection.section_title}
          </h2>

          <div style={{ fontSize: '1rem', lineHeight: '1.8' }}>
            {formatContent(currentSection.content)}
          </div>

          {/* Boutons Précédent / Suivant */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '30px',
            paddingTop: '20px',
            borderTop: '2px solid #f0f0f0'
          }}>
            <button
              onClick={goToPrev}
              disabled={currentIndex <= 0}
              style={{
                padding: '12px 28px',
                border: 'none',
                borderRadius: '8px',
                cursor: currentIndex > 0 ? 'pointer' : 'not-allowed',
                fontWeight: '700',
                fontSize: '15px',
                background: currentIndex > 0 ? '#f0f2f8' : '#f5f5f5',
                color: currentIndex > 0 ? '#333' : '#bbb',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span style={{ fontSize: '18px' }}>&larr;</span>
              {currentIndex > 0 ? sections[currentIndex - 1].section_title.replace(/^\d+\.\s*/, '') : 'Précédent'}
            </button>

            <span style={{
              color: '#999',
              fontWeight: '600',
              fontSize: '14px'
            }}>
              {currentIndex + 1} / {sections.length}
            </span>

            <button
              onClick={goToNext}
              disabled={currentIndex >= sections.length - 1}
              style={{
                padding: '12px 28px',
                border: 'none',
                borderRadius: '8px',
                cursor: currentIndex < sections.length - 1 ? 'pointer' : 'not-allowed',
                fontWeight: '700',
                fontSize: '15px',
                background: currentIndex < sections.length - 1
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : '#f5f5f5',
                color: currentIndex < sections.length - 1 ? 'white' : '#bbb',
                boxShadow: currentIndex < sections.length - 1 ? '0 2px 8px rgba(102, 126, 234, 0.3)' : 'none',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {currentIndex < sections.length - 1 ? sections[currentIndex + 1].section_title.replace(/^\d+\.\s*/, '') : 'Suivant'}
              <span style={{ fontSize: '18px' }}>&rarr;</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScriptAppel;
